"use client";

import { useEffect, useState } from "react";
import { UserMinus, Eye, Edit, Clock, CheckCircle } from "lucide-react";

interface Collaborator {
  id: string;
  email: string | null;
  role: string;
  invitedAt: string;
  acceptedAt: string | null;
  user: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  } | null;
}

interface CollaboratorListProps {
  sessionId: string;
  isOwner: boolean;
}

export default function CollaboratorList({
  sessionId,
  isOwner,
}: CollaboratorListProps) {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    fetchCollaborators();
  }, [sessionId]);

  const fetchCollaborators = async () => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}/collaborators`);
      if (response.ok) {
        const data = await response.json();
        setCollaborators(data.collaborators || []);
      }
    } catch (error) {
      console.error("Failed to fetch collaborators:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (collaboratorId: string) => {
    if (!confirm("Are you sure you want to revoke this user's access?")) {
      return;
    }

    setRevoking(collaboratorId);

    try {
      const response = await fetch(
        `/api/sessions/${sessionId}/collaborators/${collaboratorId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        // Remove from list
        setCollaborators((prev) =>
          prev.filter((c) => c.id !== collaboratorId)
        );
      } else {
        const data = await response.json();
        alert(data.error || "Failed to revoke access");
      }
    } catch (error) {
      console.error("Failed to revoke access:", error);
      alert("Failed to revoke access");
    } finally {
      setRevoking(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading collaborators...
      </div>
    );
  }

  if (collaborators.length === 0) {
    return (
      <div className="text-center py-8 border-4 border-dashed border-black/20 rounded-think-sm">
        <p className="text-gray-500">No collaborators yet</p>
        <p className="text-sm text-gray-400 mt-2">
          Use the Invite tab to add collaborators
        </p>
      </div>
    );
  }

  const activeCollaborators = collaborators.filter((c) => c.acceptedAt);
  const pendingInvites = collaborators.filter((c) => !c.acceptedAt);

  return (
    <div className="stack gap-6">
      {/* Active Collaborators */}
      {activeCollaborators.length > 0 && (
        <div className="stack gap-3">
          <h4 className="text-sm font-black uppercase text-gray-600 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Active Collaborators ({activeCollaborators.length})
          </h4>

          <div className="stack gap-2">
            {activeCollaborators.map((collab) => (
              <div
                key={collab.id}
                className="border-4 border-black p-4 bg-white flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  {collab.user?.image ? (
                    <img
                      src={collab.user.image}
                      alt={collab.user.name || "User"}
                      className="w-10 h-10 rounded-full border-2 border-black"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full border-2 border-black bg-gray-200 flex items-center justify-center font-black text-gray-600">
                      {collab.user?.name?.[0]?.toUpperCase() ||
                        collab.email?.[0]?.toUpperCase() ||
                        "?"}
                    </div>
                  )}

                  {/* Info */}
                  <div className="stack gap-1">
                    <p className="font-bold">
                      {collab.user?.name || collab.email}
                    </p>
                    {collab.user?.name && (
                      <p className="text-sm text-gray-600">{collab.email}</p>
                    )}
                  </div>
                </div>

                {/* Role & Actions */}
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 border-2 border-black text-xs font-black uppercase flex items-center gap-1 ${
                      collab.role === "edit"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {collab.role === "edit" ? (
                      <>
                        <Edit className="w-3 h-3" />
                        Can Edit
                      </>
                    ) : (
                      <>
                        <Eye className="w-3 h-3" />
                        View Only
                      </>
                    )}
                  </span>

                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(collab.id)}
                      disabled={revoking === collab.id}
                      className="p-2 border-2 border-black hover:bg-red-500 hover:text-white transition disabled:opacity-50"
                      title="Revoke access"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="stack gap-3">
          <h4 className="text-sm font-black uppercase text-gray-600 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invites ({pendingInvites.length})
          </h4>

          <div className="stack gap-2">
            {pendingInvites.map((collab) => (
              <div
                key={collab.id}
                className="border-4 border-dashed border-black/30 p-4 bg-gray-50 flex items-center justify-between"
              >
                <div className="stack gap-1">
                  <p className="font-bold">{collab.email}</p>
                  <p className="text-xs text-gray-500">
                    Invited {new Date(collab.invitedAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 border-2 border-black/20 text-xs font-black uppercase ${
                      collab.role === "edit"
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {collab.role === "edit" ? "Can Edit" : "View Only"}
                  </span>

                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(collab.id)}
                      disabled={revoking === collab.id}
                      className="p-2 border-2 border-black/20 hover:bg-red-500 hover:text-white transition disabled:opacity-50"
                      title="Cancel invite"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
